import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFBX, useTexture } from "@react-three/drei";
import { useSafeGLTF } from "../../../../utils/safeGLTFLoader";
import useInitDecor from "../hooks/useInitDecor";
import { Box3, BoxGeometry, DoubleSide, Group, Mesh, MeshStandardMaterial, Vector3, ExtrudeGeometry, Shape, Vector2, Color } from "three";
import { DecorItemI } from "../../../../shared";
import { useHDR } from "../../../../hooks";
import { useColors } from "../../../../hooks/useColors";
import { updateDoorMaterials } from "./updateMaterials/updateDoorMaterials";
import { updateWindowMaterials } from "./updateMaterials/updateWindowMaterials";
import { updateOtherMaterials } from "./updateMaterials/updateOtherMaterials";
import { createWireframe } from "./createWireframe";
import { useStoreRoom, useStoreSystem } from "../../../../store";
import { GLTFResult } from "../../room/ImportedRoom";
import { isTriangularPrism } from "../../../../utils/createTriangularPrism";
import { requiresPreciseWorkbenchToolboxBounds } from "../../../../utils/workbenchToolbox";

const needEnv = ["Toyota"];
const textureWhiteUrl = `${process.env.PUBLIC_URL}/assets/textures/door/white.jpg`;
const aomapUrl = `${process.env.PUBLIC_URL}/assets/textures/door/aomap.png`;

interface PrimitiveProps {
    decor: DecorItemI;
    visible: boolean;
    gltfRoom: GLTFResult;
}

const GREEN_WALL_COLOR = "#00ff00";
const GREEN_DECOR_NAMES = ["Вентиляционный короб"];
const ORIGINAL_COLOR_USER_DATA_KEY = "__originalVentilationBoxColor";

const normalizeDecorName = (name?: string) => name?.trim().toLowerCase() ?? "";

const shouldPaintDecorGreen = (decor: DecorItemI) => {
    const decorName = normalizeDecorName(decor.name);

    return GREEN_DECOR_NAMES.some((name) => decorName === normalizeDecorName(name));
};

const applyGreenColorToDecorOwnMaterials = (object: Group, isGreen: boolean) => {
    object.traverse((elem) => {
        if (!(elem as Mesh).isMesh) {
            return;
        }

        const mesh = elem as Mesh;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

        mesh.receiveShadow = true;
        mesh.castShadow = true;

        materials.forEach((material) => {
            const materialWithColor = material as MeshStandardMaterial;

            if (!materialWithColor?.color) {
                return;
            }

            if (!materialWithColor.userData[ORIGINAL_COLOR_USER_DATA_KEY]) {
                materialWithColor.userData[ORIGINAL_COLOR_USER_DATA_KEY] = materialWithColor.color.getHexString();
            }

            const originalColor = materialWithColor.userData[ORIGINAL_COLOR_USER_DATA_KEY];

            materialWithColor.color.set(isGreen ? GREEN_WALL_COLOR : `#${originalColor}`);
            materialWithColor.needsUpdate = true;
        });
    });
};

const normalizeSearchValue = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const isWorkbenchToolboxDecor = (decor: DecorItemI) => {
    const searchValue = [decor.name, decor.code, decor.model, decor.modelId, decor.bitrixId]
      .map(normalizeSearchValue)
      .join(" ");

    return (
      searchValue.includes("верстак туллбокс") ||
      searchValue.includes("верстак тулбокс") ||
      (searchValue.includes("верстак") && searchValue.includes("тул")) ||
      searchValue.includes("workbench toolbox")
    );
};

const applyWorkbenchToolboxMaterialsColors = (decor: DecorItemI, object: Group, colorElementColor: Color) => {
    if (!isWorkbenchToolboxDecor(decor)) {
        return;
    }

    object.traverse((elem) => {
        if (!(elem as Mesh).isMesh) {
            return;
        }

        const mesh = elem as Mesh;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

        materials.forEach((material) => {
            const materialWithColor = material as MeshStandardMaterial;
            const materialName = materialWithColor?.name?.toLowerCase() ?? "";

            if (materialName.includes("metal")) {
                materialWithColor.color.copy(new Color(0x575D5E));
                materialWithColor.roughness = 0.8;
                materialWithColor.metalness = 0.5;
                materialWithColor.opacity = 1;
                materialWithColor.needsUpdate = true;
            } else if (materialName.includes('stol')) {
                materialWithColor.color.copy(new Color(0xFFFFFF));
                materialWithColor.roughness = 0.5;
                materialWithColor.metalness = 0.7;
                materialWithColor.opacity = 1;
                materialWithColor.needsUpdate = true;
            } else if (materialName.includes("color_element")) {
                materialWithColor.color.copy(colorElementColor);
                materialWithColor.roughness = 0.8;
                materialWithColor.metalness = 0.8;
                materialWithColor.opacity = 1;
                materialWithColor.needsUpdate = true;
            }
        });
    });
};

export const GLBPrimitive: React.FC<PrimitiveProps> = ({ decor, visible }) => {
    const model = useSafeGLTF(decor.model);
    const object = model.scene;
    const wireframeRef = useRef<Group | null>(null);
    const { scale } = useInitDecor(
        decor,
        new Box3().setFromObject(object, requiresPreciseWorkbenchToolboxBounds(decor))
    );
    const { hdrTexture } = useHDR();
    const textures = useTexture({ textureWhiteDoor: textureWhiteUrl, aomapDoor: aomapUrl });
    const [isMaterialsLoaded, setIsMaterialsLoaded] = useState(false);
    const isWallsGreen = useStoreSystem((state) => state.isWallsGreen);
    const { colorsItems, colorsShade } = useColors();

    useEffect(() => {
        if (needEnv.includes(decor.name) && hdrTexture) {
            Object.entries(model.materials).forEach((material) => {
                material[1].envMap = hdrTexture;
            });
        }
    }, [decor.name, hdrTexture, model.materials]);

    useEffect(() => {
        if (decor.type === "doors" && !object.userData.isMapLoaded) {
            updateDoorMaterials(object, textures);

            if (shouldPaintDecorGreen(decor)) {
                applyGreenColorToDecorOwnMaterials(object, isWallsGreen);
            }

            return;
        }
        if (decor.type === "windows" && !object.userData.isMapLoaded && !isMaterialsLoaded) {
            updateWindowMaterials(object).then(() => {
                setIsMaterialsLoaded(true);

                if (shouldPaintDecorGreen(decor)) {
                    applyGreenColorToDecorOwnMaterials(object, isWallsGreen);
                }
            });
            return;
        }
        if (!object.userData.isMapLoaded) {
            updateOtherMaterials(object);
        }

        if (shouldPaintDecorGreen(decor)) {
            applyGreenColorToDecorOwnMaterials(object, isWallsGreen);
        }

        applyWorkbenchToolboxMaterialsColors(
          decor,
          object,
          colorsShade[colorsItems.workbenchToolbox]
        );
    }, [decor, object, textures, isMaterialsLoaded, isWallsGreen, colorsItems.workbenchToolbox, colorsShade]);

    const wireframe = useMemo(() => {
        return createWireframe(object);
    }, [object]);

    return visible ? (
      <primitive scale={scale} object={object.clone()} />
    ) : (
      <primitive scale={scale} object={wireframe} />
    );
};

export const FBXPrimitive: React.FC<PrimitiveProps> = ({ decor, visible }) => {
    const object = useFBX(decor.model);

    const { scale } = useInitDecor(decor, new Box3().setFromObject(object));
    const textures = useTexture({ textureWhiteDoor: textureWhiteUrl, aomapDoor: aomapUrl });
    const isWallsGreen = useStoreSystem((state) => state.isWallsGreen);
    const { colorsItems, colorsShade } = useColors();

    useEffect(() => {
        if (decor.type === "doors" && !object.userData.isMapLoaded) {
            updateDoorMaterials(object, textures);

            if (shouldPaintDecorGreen(decor)) {
                applyGreenColorToDecorOwnMaterials(object, isWallsGreen);
            }

            return;
        }
        if (decor.type === "widnows" && !object.userData.isMapLoaded) {
            updateWindowMaterials(object);

            if (shouldPaintDecorGreen(decor)) {
                applyGreenColorToDecorOwnMaterials(object, isWallsGreen);
            }

            return;
        }
        if (!object.userData.isMapLoaded) {
            updateOtherMaterials(object);
        }

        if (shouldPaintDecorGreen(decor)) {
            applyGreenColorToDecorOwnMaterials(object, isWallsGreen);
        }

        applyWorkbenchToolboxMaterialsColors(
          decor,
          object,
          colorsShade[colorsItems.workbenchToolbox]
        );
    }, [decor, object, textures, isWallsGreen, colorsItems.workbenchToolbox, colorsShade]);

    const wireframe = useMemo(() => {
        return createWireframe(object);
    }, [object]);

    return visible ? (
      <primitive scale={scale} object={object.clone()} />
    ) : (
      <primitive scale={scale} object={wireframe} />
    );
};

// Создание треугольной призмы для перекрытий
export const TriangularPrismPrimitive: React.FC<PrimitiveProps> = ({ decor, visible, gltfRoom }) => {
    const { width, height, depth } = decor;
    const { materials } = gltfRoom;

    const triangularGeometry = useMemo(() => {
        if (!isTriangularPrism(decor) || !decor.triangularPrism) {
            return null;
        }

        // Создаем треугольную форму  в зависимости от направления отсечения
        const shape = new Shape();

        // Размеры для треугольника в плоскости ZY
        const d = depth / 2;   // половина глубины
        const h = height;      // высота

        let triangleVertices: Vector2[];

        switch (decor.triangularPrism.triangleDirection) {
            case 'top-right':
                // Треугольник с гипотенузой снизу (от правого нижнего к левому нижнему) и вершиной справа сверху
                triangleVertices = [
                    new Vector2(-d, 0),   // левый нижний
                    new Vector2(d, 0),    // правый нижний
                    new Vector2(d, h)     // правый верхний
                ];
                break;

            case 'top-left':
                // Треугольник с гипотенузой снизу и вершиной слева сверху
                triangleVertices = [
                    new Vector2(-d, 0),   // левый нижний
                    new Vector2(d, 0),    // правый нижний
                    new Vector2(-d, h)    // левый верхний
                ];
                break;

            case 'bottom-right':
                // Треугольник с гипотенузой сверху и вершиной справа снизу
                triangleVertices = [
                    new Vector2(-d, h),   // левый верхний
                    new Vector2(d, h),    // правый верхний
                    new Vector2(d, 0)     // правый нижний
                ];
                break;

            case 'bottom-left':
            default:
                // Треугольник с гипотенузой сверху и вершиной слева снизу
                triangleVertices = [
                    new Vector2(-d, h),   // левый верхний
                    new Vector2(d, h),    // правый верхний
                    new Vector2(-d, 0)    // левый нижний
                ];
                break;
        }

        // Создаем треугольную форму
        shape.moveTo(triangleVertices[0].x, triangleVertices[0].y);
        for (let i = 1; i < triangleVertices.length; i++) {
            shape.lineTo(triangleVertices[i].x, triangleVertices[i].y);
        }
        shape.lineTo(triangleVertices[0].x, triangleVertices[0].y); // замыкаем форму

        // Настройки экструзии
        const extrudeSettings = {
            depth: width,           // "выдавливаем" на ширину
            bevelEnabled: false     // без скруглений
        };

        // Создаем геометрию
        const geometry = new ExtrudeGeometry(shape, extrudeSettings);

        // Поворачиваем призму в правильную ориентацию
        geometry.rotateY(Math.PI / 2);     // поворот на 90° вокруг Y

        // Сдвигаем центр геометрии на половину габаритов вниз и вправо
        geometry.translate(0, -height / 2, -depth / 2);

        return geometry;

    }, [decor, depth, height, width]);

    const triangularMesh = useMemo(() => {
        if (!triangularGeometry) return null;


        const material = new MeshStandardMaterial({
            color: 0xdddddd,
            roughness: 0.8,
            metalness: 0.0,
            side: DoubleSide,
            flatShading: false,
            transparent: false,
            opacity: 1.0
        });

        const mesh = new Mesh(triangularGeometry, material);


        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.position.set(-width/2, 0, +depth/2);

        return mesh;
    }, [depth, triangularGeometry, width]);

    if (!triangularMesh) return null;

    return <primitive object={triangularMesh} />;
};

export const ConstructionsPrimitive: React.FC<PrimitiveProps> = ({ decor, visible, gltfRoom }) => {
    const roomLength = useStoreRoom.getState().length;
    const roomDepth = useStoreRoom.getState().depth;
    const roomHeight = useStoreRoom.getState().height;
    const updateDecor = useStoreRoom((state) => state.updateDecor);
    const isWallsGreen = useStoreSystem((state) => state.isWallsGreen);

    const [scale, setScale] = useState<number[]>([1, 1, 1]);

    const { materials } = gltfRoom;

    const object = useMemo(() => {

        const width = ["front", "back"].includes(decor.side) ? roomLength / 1000 : roomDepth / 1000;
        const height = decor.code === "vnutrennyaya-stena" ? roomHeight / 1000 : 0.2;
        const depth = decor.code === "vnutrennyaya-stena" ? 0.2 : 1;
        const material = shouldPaintDecorGreen(decor)
          ? new MeshStandardMaterial({
              color: isWallsGreen ? GREEN_WALL_COLOR : "#dddddd",
              roughness: 0.8,
              metalness: 0,
          })
          : materials.steny;

        const group = new Group();
        group.add(new Mesh(new BoxGeometry(width, height, depth), material));

        return group;
    }, [decor, isWallsGreen, materials.steny, roomDepth, roomHeight, roomLength]);

    const size = new Box3().setFromObject(object).getSize(new Vector3());

    useEffect(() => {
        const id = setTimeout(() => {
            if (!decor.initialized) {
                const positionZ =
                  decor.side === "front"
                    ? decor.position[2] + (decor.name === "Ворота" ? 0 : size.z / 2)
                    : decor.side === "back"
                      ? decor.position[2] - (decor.name === "Ворота" ? 0 : size.z / 2)
                      : decor.position[2];
                const positionX =
                  decor.side === "left"
                    ? decor.position[0] + (decor.name === "Ворота" ? 0 : size.x / 2)
                    : decor.side === "right"
                      ? decor.position[0] - (decor.name === "Ворота" ? 0 : size.x / 2)
                      : decor.position[0];

                const positionY = decor.type !== "doors" && decor.surface === "wall" ? decor.position[1] : size.y / 2;

                updateDecor({
                    ...decor,
                    initialized: true,
                    width: size.x,
                    height: size.y,
                    depth: size.z,
                    position: [positionX, positionY, positionZ],
                    translate: [0, 0, 0],
                });
            }
        }, 0);

        return () => clearTimeout(id);
    }, [decor, size.x, size.y, size.z, updateDecor]);

    useEffect(() => {
        if (decor.initialized) {
            setScale([decor.width / size.x, decor.height / size.y, decor.depth / size.z]);
        }
    }, [decor, size.x, size.y, size.z]);

    const wireframe = useMemo(() => {
        return createWireframe(object);
    }, [object]);

    return visible ? (
      <primitive scale={scale} object={object.clone()} />
    ) : (
      <primitive scale={scale} object={wireframe} />
    );
};
